import DealPayments from '@/components/deal/payments/deal-payments';
import DealAssignedTasks from '@/components/deal/tasks/deal-assigned-tasks';
import DealTaskSelector from '@/components/deal/tasks/deal-task-selector';
import GeneralTaskSelectorV2 from '@/components/deal/tasks/general-task-selector-v2.tsx';
import EmojiReactions from '@/components/ui/emojis/emoji-reactions.tsx';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags.ts';
import { usePreserveScrollPosition } from '@/hooks/use-redirect-to-informative-deal-url';
import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import { Deal } from '@/models/registry';
import { EmojiReactionType, TaskableType } from '@/models/types';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import DealUploadButton from './deal-upload-button';
import { NonMobileActivityFeed } from './non-mobile-activity-feed';
import { NonMobileCustomerRow } from './non-mobile-customer-row';
import { NonMobileVehicleRow } from './non-mobile-vehicle-row';
import { NonMobileVehicleSwapDialog } from './non-mobile-vehicle-swap-dialog';

export const Row = ({ children }: { children: ReactNode }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>
  );
};

export const Column = ({
  children,
  align,
}: {
  children: ReactNode;
  align: 'start' | 'end';
}) => {
  return (
    <div
      className={cn('flex flex-row', {
        'justify-start': align === 'start',
        'lg:justify-end': align === 'end',
      })}
    >
      <div className="flex flex-col gap-2 w-full min-w-56 max-w-96">
        {children}
      </div>
    </div>
  );
};

const NonMobileMain = observer(
  ({ deal, showCoregistrant }: { deal: Deal; showCoregistrant: boolean }) => {
    const { scrollContainerRef, onScroll } = usePreserveScrollPosition({
      dealId: deal.id,
    });

    const [openVehicleSwapDialog, setOpenVehicleSwapDialog] = useState(false);

    const { isFeatureEnabled: isTasksV2Enabled } = useIsFeatureEnabled({
      featureFlagName: FEATURE_FLAG_NAMES.GENERALIZED_TASKS,
    });

    useHotkeys(HOTKEYS.DEAL_VEHICLE_SWAP, (e) => {
      e.preventDefault();
      e.stopPropagation();

      setOpenVehicleSwapDialog(true);
    });

    return (
      // TODO: tie the h-calc on this to the header height
      <div
        className="flex flex-col gap-2 p-4 h-[calc(100vh-40px)] overflow-auto w-full dark:bg-dark"
        ref={scrollContainerRef}
        onScroll={onScroll}
        id="deal-scroll-container"
      >
        <NonMobileCustomerRow deal={deal} showCoregistrant={showCoregistrant} />

        <div className="w-full border-t"></div>

        <NonMobileVehicleRow
          deal={deal}
          setOpenVehicleSwapDialog={setOpenVehicleSwapDialog}
        />

        <div className="w-full border-t"></div>

        <Row>
          <Column align="start">
            <DealPayments deal={deal} />
          </Column>
        </Row>

        <div className="w-full border-t"></div>

        <div className="flex flex-col gap-1">
          <div className="flex flex-row gap-2 items-center relative p-2">
            <div className="flex-1">
              <EmojiReactions
                targetId={deal.id}
                targetType={EmojiReactionType.Deal}
              />
            </div>
            <div
              className="absolute right-3"
              style={{ top: '1.25rem', transform: 'translateY(-50%)' }}
            >
              <DealUploadButton dealId={deal.id} />
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full">
            {isTasksV2Enabled ? (
              <GeneralTaskSelectorV2
                taskableId={deal.id}
                taskableType={TaskableType.Deal}
                withHotkeyListener
                hotkey={HOTKEYS.DEAL_ADD_TASK}
              />
            ) : (
              <DealTaskSelector deal={deal} withHokeyListener />
            )}
            <DealAssignedTasks deal={deal} />
          </div>
        </div>

        <div className="w-full border-t"></div>

        <NonMobileActivityFeed deal={deal} />

        <NonMobileVehicleSwapDialog
          deal={deal}
          openVehicleSwapDialog={openVehicleSwapDialog}
          setOpenVehicleSwapDialog={setOpenVehicleSwapDialog}
        />
      </div>
    );
  },
);

export default NonMobileMain;
